import { DM_Sans, DM_Serif_Text } from 'next/font/google';
import {
  type Breakpoint,
  experimental_extendTheme as extendTheme,
  lighten
} from '@mui/material/styles';
import { Theme } from '@mui/material/styles';

import { deepmerge } from '@mui/utils';

import generateGridStyles from './mixins/generateGridStyles';
import applyBackgroundColor from './mixins/applyBackgroundColor';
import applyColorSchemeOverlay from './mixins/applyColorSchemeOverlay';
import themeComponents from './theme.components';

import './theme.types';

export const dmSans = DM_Sans({
  weight: ['400', '500'],
  subsets: ['latin']
});

export const dmSerifText = DM_Serif_Text({
  weight: '400',
  subsets: ['latin']
});

export const mainColors = ['navyBlue', 'blue', 'green', 'orange'];

const defaultSpacing = 8;
const defaultBorderRadius = 4;

export const neutralColors = {
  black: '#000',
  white: '#fff',
  surface: '#FAF7F4'
};

export const primaryColors = {
  navyBlue: '#262A48',
  grayBlue: '#C2D1DB',
  lighterGrayBlue: '#E1E8ED',
  blue: '#329FE7',
  lightBlue: '#BAE5F3',
  lighterBlue: '#DDF2F9',
  green: '#00CC8B',
  lightGreen: '#BCEFD8',
  lighterGreen: '#DEF7EC',
  orange: '#FB9200',
  lightOrange: '#FEDEB2',
  lighterOrange: '#FFEFD9'
};

export const secondaryColors = {
  red: '#FD4749',
  lightRed: '#FEC8C8',
  lighterRed: '#FFE4E4',
  purple: '#A872FF',
  lightPurple: '#DDC5F7',
  lighterPurple: '#ECDEF5'
};

const gradientColors = {
  blue: 'linear-gradient(90deg, #329FE7 , #BCEFD8 )',
  green: 'linear-gradient(90deg, #00CC8B , #BAE5F3 )',
  orange: 'linear-gradient(90deg, #FB9200 , #FEC8C8 )',
  master: 'linear-gradient(270deg, #A872FF, #FD4749, #FB9200, #00CC8B, #329FE7)',
  politicsContent: 'linear-gradient(90deg, #329FE7, #A84DD8, #FD4749)'
};

const schemes = {
  black: {
    primary: {
      main: neutralColors.black,
      contrastText: neutralColors.white,
      light: neutralColors.black,
      lighter: neutralColors.black,
      dark: neutralColors.black
    },
    secondary: {
      main: neutralColors.surface,
      contrastText: primaryColors.navyBlue,
      light: neutralColors.white,
      lighter: neutralColors.white,
      dark: neutralColors.black
    },
    highlightColor: primaryColors.navyBlue
  },
  white: {
    primary: {
      main: neutralColors.white,
      contrastText: primaryColors.navyBlue,
      light: neutralColors.white,
      lighter: neutralColors.white,
      dark: neutralColors.surface
    },
    secondary: {
      main: neutralColors.surface,
      contrastText: primaryColors.navyBlue,
      light: neutralColors.white,
      lighter: neutralColors.white,
      dark: neutralColors.black
    },
    highlightColor: primaryColors.navyBlue
  },
  surface: {
    primary: {
      main: neutralColors.surface,
      contrastText: primaryColors.navyBlue,
      light: lighten(neutralColors.surface, 0.3),
      lighter: lighten(neutralColors.surface, 0.6),
      dark: neutralColors.black
    },
    secondary: {
      main: neutralColors.surface,
      contrastText: primaryColors.navyBlue,
      light: neutralColors.white,
      lighter: neutralColors.white,
      dark: neutralColors.black
    },
    highlightColor: primaryColors.navyBlue
  },
  navyBlue: {
    primary: {
      main: primaryColors.navyBlue,
      contrastText: neutralColors.white,
      light: primaryColors.grayBlue,
      lighter: primaryColors.lighterGrayBlue,
      dark: neutralColors.black
    },
    secondary: {
      main: primaryColors.blue,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    highlightColor: primaryColors.navyBlue
  },
  blue: {
    primary: {
      main: primaryColors.blue,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: primaryColors.green,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightGreen,
      lighter: primaryColors.lighterGreen,
      dark: primaryColors.navyBlue
    },
    highlightColor: primaryColors.blue
  },
  lighterBlue: {
    primary: {
      main: primaryColors.lighterBlue,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lighterBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: primaryColors.green,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightGreen,
      lighter: primaryColors.lighterGreen,
      dark: primaryColors.navyBlue
    },
    highlightColor: primaryColors.blue
  },
  green: {
    primary: {
      main: primaryColors.green,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightGreen,
      lighter: primaryColors.lighterGreen,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: primaryColors.orange,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightOrange,
      lighter: primaryColors.lighterOrange,
      dark: primaryColors.navyBlue
    },
    highlightColor: primaryColors.green
  },
  orange: {
    primary: {
      main: primaryColors.orange,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightOrange,
      lighter: primaryColors.lighterOrange,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: primaryColors.navyBlue,
      contrastText: neutralColors.white,
      light: neutralColors.white,
      lighter: primaryColors.grayBlue,
      dark: primaryColors.navyBlue
    },
    highlightColor: primaryColors.orange
  },
  blueGradient: {
    primary: {
      main: gradientColors.blue,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: gradientColors.green,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightGreen,
      lighter: primaryColors.lighterGreen,
      dark: primaryColors.navyBlue
    },
    highlightColor: gradientColors.blue
  },
  greenGradient: {
    primary: {
      main: gradientColors.green,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightGreen,
      lighter: primaryColors.lighterGreen,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: gradientColors.orange,
      contrastText: neutralColors.black,
      light: primaryColors.lightOrange,
      lighter: primaryColors.lighterOrange,
      dark: primaryColors.navyBlue
    },
    highlightColor: gradientColors.green
  },
  orangeGradient: {
    primary: {
      main: gradientColors.orange,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightOrange,
      lighter: primaryColors.lighterOrange,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: gradientColors.blue,
      contrastText: neutralColors.white,
      light: primaryColors.lightBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    highlightColor: gradientColors.orange
  },
  masterGradient: {
    primary: {
      main: gradientColors.master,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: gradientColors.politicsContent,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    highlightColor: gradientColors.master
  },
  politicsContentGradient: {
    primary: {
      main: gradientColors.politicsContent,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    secondary: {
      main: gradientColors.master,
      contrastText: primaryColors.navyBlue,
      light: primaryColors.lightBlue,
      lighter: primaryColors.lighterBlue,
      dark: primaryColors.navyBlue
    },
    highlightColor: gradientColors.politicsContent
  }
};

export const breakpointsMinMax: Record<string, { min: number; max: number }> = {
  xs: { min: 0, max: 580 },
  sm: { min: 580, max: 834 },
  md: { min: 834, max: 1024 },
  lg: { min: 1024, max: 1440 },
  xl: { min: 1440, max: 1920 },
  xxl: { min: 1920, max: 3840 }
};

const paletteTheme = {
  breakpoints: {
    values: {
      xs: breakpointsMinMax.xs.min,
      sm: breakpointsMinMax.sm.min,
      md: breakpointsMinMax.md.min,
      lg: breakpointsMinMax.lg.min,
      xl: breakpointsMinMax.xl.min,
      xxl: breakpointsMinMax.xxl.min
    }
  },
  colorSchemes: {
    light: {
      palette: {
        schemes: schemes,
        ...schemes['surface']
      }
    }
    // dark: {
    //   palette: {
    //     schemes: schemes,
    //     ...schemes['black']
    //   }
    // }
  }
};

const muiTheme = extendTheme(paletteTheme);

const baseTheme = {
  ...paletteTheme,
  spacing: defaultSpacing,
  shape: {
    borderRadius: defaultBorderRadius
  },
  mixins: {
    generateGridStyles,
    applyBackgroundColor,
    applyColorSchemeOverlay
  },
  typography: {
    h1: {
      fontFamily: dmSerifText.style.fontFamily,
      fontWeight: 'var(--h1-font-weight)',
      fontSize: 'var(--h1-font-size)',
      lineHeight: 'var(--h1-line-height)',
      // marginBottom: 'var(--h1-margin)',
      fontStyle: 'normal',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    h2: {
      fontFamily: dmSerifText.style.fontFamily,
      fontWeight: 'var(--h2-font-weight)',
      fontSize: 'var(--h2-font-size)',
      lineHeight: 'var(--h2-line-height)',
      // marginBottom: 'var(--h2-margin)',
      fontStyle: 'normal',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    h3: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--h3-font-weight)',
      fontSize: 'var(--h3-font-size)',
      lineHeight: 'var(--h3-line-height)',
      // marginBottom: 'var(--h3-margin)',
      fontStyle: 'normal',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    h4: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--h4-font-weight)',
      fontSize: 'var(--h4-font-size)',
      lineHeight: 'var(--h4-line-height)',
      // marginBottom: 'var(--h4-margin)',
      fontStyle: 'normal',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    h5: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--h5-font-weight)',
      fontSize: 'var(--h5-font-size)',
      lineHeight: 'var(--h5-line-height)',
      // marginBottom: 'var(--h5-margin)',
      fontStyle: 'normal',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    h6: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--h6-font-weight)',
      fontSize: 'var(--h6-font-size)',
      lineHeight: 'var(--h6-line-height)',
      // marginBottom: 'var(--h6-margin)',
      fontStyle: 'normal',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    subtitle1: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--subtitle1-font-weight)',
      fontSize: 'var(--subtitle1-font-size)',
      lineHeight: 'var(--subtitle1-line-height)',
      // marginBottom: 'var(--subtitle1-margin)',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    subtitle2: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--subtitle2-font-weight)',
      fontSize: 'var(--subtitle2-font-size)',
      lineHeight: 'var(--subtitle2-line-height)',
      // marginBottom: 'var(--subtitle2-margin)',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    body1: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--body1-font-weight)',
      fontSize: 'var(--body1-font-size)',
      lineHeight: 'var(--body1-line-height)',
      // marginBottom: 'var(--body1-margin)',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    body2: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--body2-font-weight)',
      fontSize: 'var(--body2-font-size)',
      lineHeight: 'var(--body2-line-height)',
      // marginBottom: 'var(--body2-margin)',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    caption: {
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--caption-font-weight)',
      fontSize: 'var(--caption-font-size)',
      lineHeight: 'var(--caption-line-height)',
      // marginBottom: 'var(--caption-margin)',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    overline: {
      display: 'block',
      fontFamily: dmSans.style.fontFamily,
      fontWeight: 'var(--overline-font-weight)',
      fontSize: 'var(--overline-font-size)',
      lineHeight: 'var(--overline-line-height)',
      textTransform: 'uppercase' as const,
      // marginBottom: 'var(--overline-margin)',
      color: 'var(--mui-palette-text-primary, inherit)'
    },
    button: {}
  },
  containerBreakpoints: {
    ...muiTheme.breakpoints,
    up: (key: Breakpoint | number) => {
      return muiTheme.breakpoints.up(key)?.replace('@media', '@container');
    },
    down: (key: Breakpoint | number) => {
      return muiTheme.breakpoints.down(key)?.replace('@media', '@container');
    }
  }
};

const coreTheme = extendTheme(baseTheme);

const typedThemeComponents = themeComponents as Record<
  string,
  (theme: Theme) => { components: Record<string, any> }
>;

export const theme = extendTheme(
  deepmerge(baseTheme, {
    components: Object.values(typedThemeComponents)
      .map((t) => t(coreTheme))
      .reduce((acc, current) => {
        return { ...acc, ...current.components };
      }, {} as Record<string, any>)
  })
);

export const breakpoints = theme.breakpoints.values;
